// Layout — corenet's shared AppShell (light 240px/72px sider, no header row),
// wired with this console's nav. Replaces the bespoke dark-rail Sidebar — see
// CLAUDE.md's app-shell swap notes for why we moved back to the canonical
// shell.
//
// No WorkspaceSwitcher (this product is single-tenant per install — see
// README's non-goals). UserMenu renders whoami's `subject` as the email
// field (there's no separate name/avatar to show) plus a role badge, and
// owns sign-out itself — no more separate bottomNav sign-out item. The
// dark-mode switch also lives in UserMenu's popover (it only renders there
// once `user` is set) — mode state is main.tsx's shared lib/themeMode.ts.
import { useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppShell, type NavItem, type NavSection } from "@bluerock/corenet/components/app-shell";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useThemeMode, setThemeMode } from "@/lib/themeMode";

type NavEntry = NavItem & { adminOnly?: boolean; mcpScan?: boolean };

// 'home' is the key AppShell looks up for brand-click navigation. Overview
// was this console's home page; removed 2026-09-03 — Host Fleet takes the
// 'home' key now, since it's the console's actual landing page (see App.tsx's
// index route).
const NAV_ITEMS: NavEntry[] = [
  { key: "home", label: "Host Fleet", icon: "Server", path: "/fleet" },
  { key: "users", label: "Users", icon: "User", path: "/users", adminOnly: true },
  { key: "policy", label: "Policy", icon: "ShieldCheck", path: "/policy" },
  // Full CCPD Agate explorer, embedded in-console at /explore (iframe → CCPD :8544/explore).
  { key: "graph-explorer", label: "Graph Explorer", icon: "Network", path: "/explore" },
  // CCPD LLM Cost Management dashboard, embedded in-console at /cost.
  { key: "cost", label: "Cost Management", icon: "DollarSign", path: "/cost" },
  { key: "identity", label: "Identity", icon: "Shield", path: "/identity", adminOnly: true },
  { key: "integrations", label: "Integrations", icon: "Cable", path: "/integrations", adminOnly: true },
  // Agentic Scan — the scan surface (runtime-flagged via whoami → mcpScanEnabled +
  // admin gate). Replaced the retired MCP Scan page.
  { key: "agentic-scan", label: "Agentic Scan", icon: "ScanSearch", path: "/agentic-scan", adminOnly: true, mcpScan: true },
];

// Advisor runs plain-HTTP internally on :8081; Caddy terminates TLS at
// :8501 so the link works from either face of the console.
function externalHref(port: number, protocol: "http" | "https", path = "/"): string {
  const portSegment =
    (protocol === "https" && port === 443) || (protocol === "http" && port === 80) ? "" : `:${port}`;
  return `${protocol}://${window.location.hostname}${portSegment}${path}`;
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, role, identity, mcpScanEnabled } = useAuth();
  const mode = useThemeMode();

  const nav: NavSection[] = useMemo(
    () => [
      {
        items: NAV_ITEMS.filter(
          (n) => (!n.adminOnly || role === "admin") && (!n.mcpScan || mcpScanEnabled),
        ),
      },
    ],
    [role, mcpScanEnabled],
  );

  const bottomNav: NavItem[] = useMemo(
    () => [
      { key: "advisor", label: "BlueRock Advisor", icon: "Bot", path: externalHref(8501, "https"), external: true },
      {
        // The FULL CCPD suite (Agate/Hydra dashboards: cost, agents, explorer).
        // CCPD serves on the host loopback (127.0.0.1:8543); Caddy fronts it on
        // :8544 of the console FQDN (valid managed cert), NSG-scoped to the
        // demo-team IPs. Opens in a new tab.
        key: "ccpd-suite",
        label: "CCPD",
        icon: "AreaChart",
        path: externalHref(8544, "https"),
        external: true,
      },
    ],
    [],
  );

  const handleSelect = (_key: string, item: NavItem) => {
    if (item.external && item.path) {
      window.open(item.path, "_blank", "noopener,noreferrer");
      return;
    }
    if (item.path) navigate(item.path);
  };

  return (
    <AppShell
      nav={nav}
      bottomNav={bottomNav}
      currentPath={location.pathname}
      onSelect={handleSelect}
      collapsePersistenceKey="bluerock:appshell:collapsed"
      // The vendored copy caught up to corenet main's 65c4537a4 "appshell:
      // remove the top header bar entirely" on the 2026-09-10 sync — AppShell
      // no longer renders a header row at all (unconditional, no prop), so
      // the explicit opt-out this comment used to justify no longer exists
      // on the type.
      user={{
        // whoami only carries `subject` + `groups` — no separate name or
        // avatar, so `subject` (the login username, shown as "Email" on
        // the login form) fills UserMenu's required `email` field.
        user: { email: identity?.subject ?? "Not signed in" },
        onSignOut: signOut,
        // admin left uncolored (neutral default) so the badge doesn't
        // read as an alert; viewer gets green for "read-only, safe".
        roleBadge: role ? { label: role.toUpperCase(), color: role === "viewer" ? "green" : undefined } : undefined,
      }}
      themeToggle={{ mode, onChange: setThemeMode }}
    >
      <Outlet />
    </AppShell>
  );
}
